import { Box, Group, ScrollArea, SimpleGrid, Stack, Text } from "@mantine/core";
import { Alert, Badge, LoadingScreen, spacing } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { ReviewImageDetail, ReviewImageSummary } from "@models/review";
import { ImageTile } from "./ImageTile";
import "./ReviewMain.scss";

interface FailedImageViewProps {
  /** The failed image summary. */
  image: ReviewImageSummary;
  /** The loaded detail, or null while loading. */
  detail: ReviewImageDetail | null;
  /** i18n key for a load error, or null. */
  loadErrorKey: string | null;
}

/**
 * The read-only main view for an image that ended FAILED (AB#955377, F9/D17).
 * It shows the SOURCE tile and the failure reason only — there is no redraw tile
 * (none was produced), no Q1 panel and no decision controls. Regeneration is
 * driven from the sidebar checkboxes, not here.
 */
export function FailedImageView({ image, detail, loadErrorKey }: FailedImageViewProps) {
  const { t } = useTranslation();

  if (loadErrorKey) {
    return (
      <Box className="review-main" p={spacing.xl}>
        <Alert color="rapidRed" title={t("review.errors.title")}>
          {t(loadErrorKey)}
        </Alert>
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box className="review-main">
        <LoadingScreen message={t("common.loading")} />
      </Box>
    );
  }

  const reasonKey = `review.failed.reason.${image.reason ?? "generation_failed"}`;

  return (
    <ScrollArea className="review-main" type="auto">
      <Stack className="review-main__column" gap={spacing.lg} p={spacing.lg}>
        <Stack gap={spacing.xs}>
          <Text fw={600} fz={18} className="review-main__title">
            {t("review.failed.viewTitle", { objId: image.objId })}
          </Text>
          <Group gap={spacing.sm} align="center" wrap="nowrap">
            <Badge color={image.regenerable ? "yellow" : "gray"} variant="light">
              {t(reasonKey, { defaultValue: image.reason ?? "generation_failed" })}
            </Badge>
            <Text size="xs" c="dimmed">
              {image.regenerable
                ? t("review.failed.regenerableHint")
                : t("review.failed.notRegenerableHint")}
            </Text>
          </Group>
        </Stack>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={spacing.md}>
          <ImageTile
            url={detail.sourceUrl}
            alt={t("review.detail.sourceAlt")}
            label={t("review.detail.source")}
            downloadName={`${image.objId}-source.png`}
          />
        </SimpleGrid>

        <Alert color="gray" title={t("review.failed.noRedrawTitle")}>
          {t("review.failed.noRedrawBody")}
        </Alert>
      </Stack>
    </ScrollArea>
  );
}
